import React, { Component } from "react";
import {
  Collapse,
  Navbar,
  NavbarBrand,
  NavbarToggler,
  NavItem,
  NavLink,
} from "reactstrap";
import { Link, NavLink as RouterNavLink, useNavigate } from "react-router-dom";
import { Authenticated, useAuth } from "../auth/AuthContext";
import { defaultPath, paths } from "../routes";

import "./NavMenu.css";

//  Order here is the nav order. Labels match each page's <h1> and browser tab exactly;
//  that agreement is the point of the whole component.
const NAV_ITEMS = [
  { to: paths.merchants, label: "Manage Merchants" },
  { to: paths.inviteMerchant, label: "Invite Merchant" },
  { to: paths.inviteHistory, label: "Invite History" },
  { to: paths.merchantReport, label: "Merchant Report", adminOnly: true },
];

const STRIPE_DASHBOARD_URL = "https://dashboard.stripe.com/dashboard";

// NavItems renders the page links. A function component so the class NavMenu can hide the
// admin-only ones without being converted; the API policy is what actually keeps
// everyone else out.
function NavItems() {
  const { isTowpayAdmin } = useAuth();

  return NAV_ITEMS.filter(({ adminOnly }) => !adminOnly || isTowpayAdmin).map(
    ({ to, label }) => (
      <NavItem key={to}>
        {/* react-router's NavLink as the tag: reactstrap hands Tag a finished className
              string, and react-router joins " active" onto a string className and sets
              aria-current="page". No `end` prop -- none of these paths is a segment
              prefix of another. Note there is deliberately no text-dark here: it is
              !important and would override --bs-navbar-active-color, making the active
              state invisible. */}
        <NavLink tag={RouterNavLink} to={to}>
          {label}
        </NavLink>
      </NavItem>
    )
  );
}

// SignOutButton clears the local session. The user service call it makes only blanks the
// stored token; the JWT stays valid until it expires, so clearing it here is what actually
// ends the session.
function SignOutButton() {
  const { email, logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await logout();
    navigate(paths.login, { replace: true });
  };

  return (
    //  A NavItem rather than a bare span so it sits in the same <ul> as the links and
    //  shares their row. As a sibling of the Collapse it was pushed onto a line of its
    //  own, because .navbar-collapse has flex-grow: 1 and claims the whole row.
    <NavItem className="d-flex align-items-center ms-sm-3 mt-2 mt-sm-0">
      {email && <span className="text-muted me-2">{email}</span>}
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={handleSignOut}
      >
        Log Out
      </button>
    </NavItem>
  );
}

export class NavMenu extends Component {
  static displayName = NavMenu.name;

  constructor(props) {
    super(props);

    this.toggleNavbar = this.toggleNavbar.bind(this);
    this.state = {
      collapsed: true,
    };
  }

  toggleNavbar() {
    this.setState({
      collapsed: !this.state.collapsed,
    });
  }

  render() {
    return (
      <header>
        {/*  `container` rather than the default container-fluid makes reactstrap render
             the .container itself, as a direct child of .navbar. That matters: the
             Bootstrap rule laying this row out is ".navbar > .container", a direct-child
             selector. Nesting our own inside reactstrap's left it a grandchild, unmatched
             by that rule and so a plain block div, which is why the brand and the links
             sat on separate lines. The CRA template hid it by making the collapse
             inline-flex. */}
        <Navbar
          className="navbar-expand-sm border-bottom box-shadow mb-3"
          container="fluid"
          light
        >
          {/* A plain Link, not a NavLink: the brand points at defaultPath rather than
                "/" so the most-clicked link in the chrome doesn't route through the
                redirect, and a NavLink to "/" would match every path and sit permanently
                active. */}
          <NavbarBrand tag={Link} to={defaultPath}>
            <img src="towpay-logo.png" className="logo" alt="TowPay Logo" />
            Merchant Management
          </NavbarBrand>
          <Authenticated>
            {/*  Inside Authenticated: with no session there is no menu, and the toggler
                   opened an empty drawer on the login screen. */}
            <NavbarToggler onClick={this.toggleNavbar} />
            <Collapse isOpen={!this.state.collapsed} navbar>
              {/*  ms-auto right-aligns the whole group against the brand, which is what
                     the template's d-sm-inline-flex + flex-sm-row-reverse was reaching for.
                     align-items-sm-center lines the Log Out button up with the text links
                     once they are side by side; stacked on mobile they go full width. */}
              <ul className="navbar-nav ms-auto align-items-sm-center">
                <NavItems />
                <NavItem>
                  <a
                    className="nav-link"
                    href={STRIPE_DASHBOARD_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Stripe Dashboard{" "}
                    <i
                      className="bi bi-box-arrow-up-right"
                      aria-hidden="true"
                    />
                  </a>
                </NavItem>
                <SignOutButton />
              </ul>
            </Collapse>
          </Authenticated>
        </Navbar>
      </header>
    );
  }
}
